import React, { type PropsWithChildren, useState } from "react";
import cx from "classnames";
import { Drawer } from "@picklebet/ui";
import { MoreInfo } from "@picklebet/ui";

import * as styles from "./BaseSelection.module.scss";

interface BaseSelectionSlots {
  icon: React.ReactNode;
  outcomeName: React.ReactNode;
  marketName: React.ReactNode;
  odds?: React.ReactNode;
  clamp?: React.ReactNode;
  drawerContent: React.ReactNode;
}

interface BaseSelectionProps extends PropsWithChildren {
  isFirst?: boolean;
  isLast?: boolean;
  isSelectionWon?: boolean;
  isEntryWon?: boolean;
  isVoid?: boolean;
  isCombo?: boolean;
  isQuaddie?: boolean;
  isCashedOut?: boolean;
  isPickem?: boolean;
  isDecided?: boolean;
  connectUp?: boolean;
  connectDown?: boolean;
  onClick?: () => void;
  slots: BaseSelectionSlots;
}

const Selection = ({
  isFirst,
  isLast,
  isSelectionWon,
  isEntryWon,
  isVoid,
  isCombo,
  isQuaddie,
  isCashedOut,
  isPickem,
  isDecided,
  connectUp,
  connectDown,
  onClick,
  slots,
  children,
}: BaseSelectionProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <div
      className={cx(styles.selection, {
        [styles.isQuaddie]: isQuaddie,
        [styles.isFirst]: isFirst,
        [styles.isLast]: isLast,
        [styles.isSelectionWon]: isSelectionWon, // round border on icon
        [styles.isEntryWon]: isEntryWon, // green line when entry won
        [styles.connectUp]: connectUp, // green top half of connector (joins to prev leg)
        [styles.connectDown]: connectDown, // green bottom half of connector (joins to next leg)
        [styles.showLineHighlight]:
          isQuaddie && (isDecided ? isEntryWon : isSelectionWon), // quaddie only: whole green line on full win
        [styles.isVoid]: isVoid,
        [styles.isCombo]: isCombo,
        [styles.isCashedOut]: isCashedOut,
        [styles.isPickem]: isPickem,
      })}
    >
      <div className={styles.iconAndDescription}>
        <div className={styles.icon}>{slots.icon}</div>
        <div
          className={cx(styles.info, {
            [styles.clickable]: !!onClick,
            [styles.isPickem]: isPickem,
          })}
          onClick={() => onClick?.()}
        >
          <span className={styles.outcomeName}>{slots.outcomeName}</span>
          <span className={styles.marketName}>{slots.marketName}</span>
          {slots.odds && <span className={styles.odds}>{slots.odds}</span>}
        </div>
        <div className={cx(styles.clamp, { [styles.isPickem]: isPickem })}>
          {slots.clamp}

          <div className={styles.caret}>
            <MoreInfo isExpanded={isExpanded} onClick={toggleExpanded} />
          </div>
        </div>
      </div>
      <Drawer isOpen={isExpanded}>
        <div className={styles.drawer}>{slots.drawerContent}</div>
      </Drawer>
      {children}
    </div>
  );
};

export { Selection };
